import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Star, Eye, EyeOff, User } from 'lucide-react'
import { api } from '@/lib/api'

interface GameSettingsItem {
  id: string
  name: string
  icon?: string
  category: string
  is_featured: boolean
  is_hidden: boolean
}

interface StudentSummary {
  student_id: number
  name: string
}

interface AllStudentsStats {
  students: StudentSummary[]
}

export default function GameManager() {
  const queryClient = useQueryClient()
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)

  // Fetch students
  const { data: allStats, isLoading } = useQuery<AllStudentsStats>({
    queryKey: ['parent-game-stats-all'],
    queryFn: async () => {
      const response = await api.get('/api/parent/game-stats/all-students')
      return response.data
    },
  })

  // Fetch game settings for selected student
  const { data: gameSettings } = useQuery<{ games: GameSettingsItem[] }>({
    queryKey: ['parent-game-settings', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}/game-settings`)
      return response.data
    },
    enabled: !!selectedStudentId,
  })

  // Toggle featured
  const toggleFeatured = useMutation({
    mutationFn: async (gameId: string) => {
      const response = await api.post(
        `/api/parent/students/${selectedStudentId}/featured-games/${gameId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-featured-games', selectedStudentId] })
      queryClient.invalidateQueries({ queryKey: ['parent-game-settings', selectedStudentId] })
    },
  })

  // Toggle hidden
  const toggleHidden = useMutation({
    mutationFn: async (gameId: string) => {
      const response = await api.post(
        `/api/parent/students/${selectedStudentId}/hidden-games/${gameId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-game-settings', selectedStudentId] })
    },
  })

  // Auto-select first student
  if (!selectedStudentId && allStats?.students.length) {
    setSelectedStudentId(allStats.students[0].student_id)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  // Group games by category
  const games = gameSettings?.games || []
  const grouped: Record<string, GameSettingsItem[]> = {}
  for (const game of games) {
    const cat = game.category || 'Other'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push(game)
  }
  const categoryOrder = ['Spelling', 'Math', 'History', 'Study']
  const sortedCategories = [
    ...categoryOrder.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !categoryOrder.includes(c)),
  ]

  return (
    <div className="space-y-6">
      {/* Student Picker */}
      {allStats && allStats.students.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {allStats.students.map((student) => (
            <button
              key={student.student_id}
              onClick={() => setSelectedStudentId(student.student_id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition ${
                selectedStudentId === student.student_id
                  ? 'bg-purple-600 text-white'
                  : 'bg-surface-card text-gray-400 hover:text-white hover:bg-surface-hover'
              }`}
            >
              <User className="w-4 h-4" />
              {student.name}
            </button>
          ))}
        </div>
      )}

      {/* Games grouped by category */}
      {sortedCategories.map((category) => (
        <div key={category} className="bg-surface-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-700/50">
            <h3 className="font-semibold">{category}</h3>
          </div>
          <div className="divide-y divide-gray-700/50">
            {grouped[category].map((game) => (
              <div
                key={game.id}
                className={`p-4 flex items-center justify-between ${
                  game.is_hidden ? 'opacity-40' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {game.icon && <span className="text-xl">{game.icon}</span>}
                  <div>
                    <div className="font-medium">{game.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleFeatured.mutate(game.id)}
                    className="p-2 rounded-lg hover:bg-surface-hover transition"
                    title={game.is_featured ? 'Remove from Featured' : 'Add to Featured'}
                  >
                    <Star
                      className={`w-5 h-5 ${
                        game.is_featured
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-600'
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => toggleHidden.mutate(game.id)}
                    className="p-2 rounded-lg hover:bg-surface-hover transition"
                    title={game.is_hidden ? 'Show game' : 'Hide game'}
                  >
                    {game.is_hidden ? (
                      <EyeOff className="w-5 h-5 text-red-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-green-400" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {games.length === 0 && selectedStudentId && (
        <div className="text-center text-gray-500 py-12">No games registered yet.</div>
      )}
    </div>
  )
}
